import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Modal,
  Rating,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import autovalid360PlanGreen from '../../assets/images/autovalid360_plan_green.svg';
import autovalid360PlanBlue from '../../assets/images/autovalid360_plan_blue.svg';
import VehicleInspections from '../single-page/api/vehicle_inspections';
import ROUTES from "../single-page/constants/routes.js";
import { useModals } from '../hooks/useModals';

function formatCreateInspectionError(err) {
  if (typeof err === 'string') {
    if (/timeout|Network Error/i.test(err)) {
      return 'Não foi possível conectar. Verifique sua internet e tente novamente.';
    }
    return 'Não foi possível concluir a solicitação. Tente novamente.';
  }
  const data = err?.data;
  if (!data || typeof data !== 'object') {
    return 'Não foi possível iniciar a vistoria. Tente novamente.';
  }
  if (data.code === 'plan_pricing_misconfigured') {
    const fromBase = data.error?.base?.[0];
    if (fromBase) return fromBase;
  }
  if (data.code === 'insufficient_credits') {
    const fromBase = data.error?.base?.[0];
    if (fromBase) return fromBase;
    return 'Seu pacote de consultas foi esgotado. Adquira novos créditos para continuar consultando.';
  }
  if (data.error && typeof data.error === 'object') {
    const flat = Object.values(data.error).flat().filter(Boolean);
    if (flat.length) {
      const first = flat[0];
      return Array.isArray(first) ? first[0] : String(first);
    }
  }
  if (typeof data.error === 'string') return data.error;
  if (typeof data.message === 'string') return data.message;
  return 'Não foi possível iniciar a vistoria. Tente novamente.';
}

const PlansModal = ({ open, onClose, licensePlate, isAuthenticated, navigate }) => {
  const { handleOpenLogin, handleClosePlans } = useModals();
  const [prices, setPrices] = useState({ basic: '', premium: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await fetch(ROUTES.PRICES)
        const data = await response.json()
        
        setPrices({
          basic: data.basic_plan,
          premium: data.premium_plan
        })
      } catch (error) {
        console.error('Erro ao carregar preços:', error)
      }
    }
    fetchPrices()
  }, [])

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const handlePlanSelection = async (planType) => {
    if (!isAuthenticated) {
      handleClosePlans();
      handleOpenLogin(licensePlate, planType);
      return;
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      const response = await VehicleInspections.create({
        license_plate: licensePlate,
        plan_type: planType
      });
      const inspectionId = response?.inspection?.inspection_id;
      if (!inspectionId) {
        setSubmitError('Resposta inválida do servidor. Tente novamente.');
        return;
      }
      onClose();
      navigate(`${ROUTES.MY_INSPECTIONS}/${inspectionId}`);
    } catch (error) {
      console.error('Erro detalhado ao criar vistoria:', {
        error,
        requestData: {
          license_plate: licensePlate,
          plan_type: planType
        }
      });
      setSubmitError(formatCreateInspectionError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="plans-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 1, sm: 2 },
        overflow: 'auto'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 4,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          m: 'auto'
        }}
      >
        {submitError ? (
          <Alert
            severity="error"
            onClose={() => setSubmitError(null)}
            sx={{ width: '100%', maxWidth: 560, mb: 2 }}
          >
            {submitError}
          </Alert>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 2, md: 3 },
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center',
            width: '100%'
          }}
        >
        {/* PREMIUM PLAN - COMENTADO TEMPORARIAMENTE
        <Box sx={{
          flex: 1,
          p: { xs: 2, sm: 3 },
          borderRadius: { xs: 3, md: 6 },
          bgcolor: 'white',
          boxShadow: 3,
          maxWidth: '100%',
          position: 'relative'
        }}>
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: { xs: '50%', md: '65px' },
            transform: {
              xs: 'translate(-50%, -100%)',
              md: 'translateY(-100%)'
            },
            width: '180px',
            p: 2,
            py: 0.7,
            borderRadius: '12px 12px 0 0',
            background: 'linear-gradient(299.59deg, #06B077 -2.27%, #1EA2B7 109.29%)',
            color: 'white',
            fontSize: '0.875rem',
            textAlign: 'center',
            zIndex: 1
          }}>
            Mais indicada
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 },
            height: '100%'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: { xs: '100%', md: 220 },
              py: { xs: 0.5, sm: 1, md: 1.5 }
            }}>
              <Rating
                value={5}
                readOnly
                sx={{
                  '& .MuiRating-iconFilled': {
                    color: '#06B077'
                  },
                  transform: { xs: 'scale(0.8)', md: 'scale(0.9)' }
                }}
              />

              <Box
                component="img"
                src={autovalid360PlanGreen}
                alt="Autovalid 360 Premium"
                sx={{
                  width: { xs: 140, sm: 160, md: 160 },
                  my: { xs: 1, sm: 2, md: 3 },
                  mt: { xs: 2, sm: 3, md: 4 }
                }}
              />

              <Divider
                sx={{
                  width: '100%',
                  background: 'linear-gradient(89.96deg, #00B26C -1.43%, #259EC9 106.6%)',
                  height: '3px',
                  my: { xs: 1.5, sm: 2, md: 3 }
                }}
              />

              <Box sx={{
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1.5, sm: 2, md: 3 },
                width: '100%'
              }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: 0.5
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                      color: 'transparent',
                      background: 'linear-gradient(89.96deg, #00B26C -1.43%, #259EC9 106.6%)',
                      backgroundClip: 'text',
                      fontWeight: 500,
                      mt: -0.5
                    }}
                  >
                    R$
                  </Typography>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                      color: 'transparent',
                      background: 'linear-gradient(89.96deg, #00B26C -1.43%, #259EC9 106.6%)',
                      backgroundClip: 'text',
                      fontWeight: 400
                    }}
                  >
                    {prices.premium}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    bgcolor: '#00B26C',
                    '&:hover': {
                      bgcolor: '#009b5e'
                    },
                    borderRadius: { xs: 2, md: 4 },
                    maxWidth: { xs: '80%', md: '70%' },
                    py: { xs: 0.75, md: 1 },
                    mx: 'auto',
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                  disabled={submitting}
                  onClick={() => {
                    handlePlanSelection('premium');
                  }}
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  Escolher
                </Button>
              </Box>
            </Box>

            <Box sx={{
              flex: 1,
              border: '2px solid #00B26C',
              borderRadius: { xs: 3, md: 6 },
              p: { xs: 2, md: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, md: 3 }
            }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Histórico Veicular
                </Typography>
                <Typography variant="body2" component="div" sx={{ pl: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  • Pesquisa de procedência<br/>
                  • Leilão<br/>
                  • Sinistro<br/>
                  • Furto<br/>
                  • Impedimentos<br/>
                  • Restrições judiciais<br/>
                  • Débitos
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' }, mt: 2 }}>
                  Identificação
                </Typography>
                <Typography variant="body2" component="div" sx={{ pl: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  • Conferência dos itens do veículo<br/>
                  • Chassi<br/>
                  • Numeração do motor<br/>
                  • Etiquetas<br/>
                  • Lacres<br/>
                  • Km<br/>
                  • Verificação de originalidade ou troca dos vidros
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Estrutura
                </Typography>
                <Typography variant="body2" component="div" sx={{ pl: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  • Análise de carroceria, pintura<br/>
                  • Indícios de colisão<br/>
                  • Repintura<br/>
                  • Lanternagem e recuperação<br/>
                  • Verificação dos pontos estruturais:<br/>
                  • Longarinas<br/>
                  • Colunas<br/>
                  • Painéis
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' }, mt: 2 }}>
                  Laudo Técnico
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Emissão de laudo com registros fotográficos de todos os itens consultados e inspecionados.
                </Typography>

                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' }, mt: 2 }}>
                  Diagnóstico
                </Typography>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  Diagnóstico completo do motor.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        FIM PREMIUM PLAN COMENTADO */}

        {/* Plano Básico */}
        <Box sx={{ 
          width: { xs: '100%', md: 320 },
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          bgcolor: 'white',
          boxShadow: '0 16px 40px rgba(0,0,0,0.28)',
          borderTop: '4px solid #51DF94',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <Rating 
            value={5} 
            readOnly 
            sx={{
              '& .MuiRating-iconFilled': {
                color: '#51DF94'
              },
              mb: { xs: 2, sm: 3, md: 4 },
              transform: { xs: 'scale(0.8)', md: 'scale(0.9)' }
            }}
          />

          <Box 
            component="img"
            src={autovalid360PlanBlue}
            alt="Autovalid 360 Básico"
            sx={{ 
              width: { xs: 140, sm: 160, md: 160 },
              mb: { xs: 1.5, sm: 2, md: 2 }
            }}
          />

          <Divider sx={{ 
            width: '100%',
            height: '3px',
            background: 'linear-gradient(135deg, #6BD3FD 0%, #51DF94 100%)',
            mb: { xs: 1.5, sm: 2, md: 2 }
          }} />

          <Box sx={{ 
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 0.5,
            mb: { xs: 1.5, sm: 2, md: 2 }
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#012F47',
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                fontWeight: 600,
                mt: -0.5
              }}
            >
              R$
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: '#012F47',
                fontFamily: 'GT Haptik, Raleway, sans-serif',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              {prices.basic}
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="contained"
            disabled={submitting}
            sx={{
              color: '#fff',
              background: 'linear-gradient(135deg, #6BD3FD 0%, #51DF94 100%)',
              mb: { xs: 2, sm: 2.5, md: 3 },
              borderRadius: 2,
              maxWidth: { xs: '80%', sm: '70%', md: '60%' },
              py: { xs: 0.75, md: 1 },
              fontSize: { xs: '0.875rem', md: '1rem' },
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              boxShadow: '0 8px 24px rgba(81, 223, 148, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6BD3FD 0%, #51DF94 100%)',
                filter: 'brightness(1.06)',
              }
            }}
            onClick={() => {
              handlePlanSelection('basic');
            }}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Escolher
          </Button>

          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 'bold', 
            }}>
              Histórico Veicular
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 1 }}>
              • Pesquisa de procedência<br/>
              • Leilão<br/>
              • Sinistro<br/>
              • Furto<br/>
              • Impedimentos<br/>
              • Restrições judiciais<br/>
              • Débitos
            </Typography>
          </Box>
        </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PlansModal;
